/**
 * SoftParticleModifier — depth-fade ("soft") particles.
 *
 * The classic technique: compare the scene depth behind the fragment with the
 * fragment's own depth and fade opacity over a world-space distance so
 * billboards never form hard knife-edge intersections with geometry:
 *
 *   fade = saturate((sceneViewZ − fragViewZ) / softDistance)
 *
 * three r185 exposes the already-rendered opaque depth to transparent passes
 * via `viewportLinearDepth` (scene, normalized 0..1 against the camera range)
 * and the fragment's own via `linearDepth()` — both linear, so the delta can
 * be scaled straight into world units with the camera far plane.
 */

import type * as THREE from 'three/webgpu';
import {
  Fn, uniform, float, viewportLinearDepth, linearDepth, saturate, cameraFar, cameraNear,
} from 'three/tsl';

export interface SoftParticleConfig {
  /** World-units over which the fade ramps (default 0.75). */
  softDistance?: number;
  /**
   * Camera far plane used to convert normalized linear depth. Omitted (the default) = the RENDERING camera's own
   * range (far − near), read per draw — exact, and right for Atlas's 120 km far plane. Pass a number only to pin it.
   */
  cameraFar?: number;
}

export interface SoftParticleHandle {
  softDistance: { value: number };
  cameraFar: { value: number };
}

/** CPU mirror (exported for tests): fade for normalized linear depths. */
export function softFadeCpu(
  sceneLinear: number, fragLinear: number, softDistance: number, cameraFar: number,
): number {
  const deltaWorld = (sceneLinear - fragLinear) * cameraFar;
  const f = deltaWorld / Math.max(softDistance, 1e-6);
  return f < 0 ? 0 : (f > 1 ? 1 : f);
}

/**
 * Patch a particle material's opacity with the depth fade. Composes with an
 * existing opacityNode (multiplies).
 */
export function applySoftParticles(
  material: THREE.Material & { opacityNode?: unknown; transparent?: boolean; depthWrite?: boolean },
  config?: SoftParticleConfig,
): SoftParticleHandle {
  const uSoft = uniform(config?.softDistance ?? 0.75);
  const uFar = uniform(config?.cameraFar ?? 1000);
  // WHY the live camera range by default: three's linear depths are normalised over (far − near) of the camera that
  // draws, and the old fixed 1000 m fallback under a 120 km far plane scaled every depth gap down 120× — a splash 2 m
  // in front of the sea drew at 2 % opacity (Atlas's water splash passes no far). A pinned far keeps its uniform.
  const range = config?.cameraFar === undefined ? cameraFar.sub(cameraNear) : uFar;

  const prevOpacity = material.opacityNode;
  material.opacityNode = Fn(() => {
    const deltaWorld = viewportLinearDepth.sub(linearDepth()).mul(range);
    const fade = saturate(deltaWorld.div(uSoft));
    const base = (prevOpacity ?? float(1)) as never;
    return float(base).mul(fade);
  })();
  material.transparent = true;
  material.depthWrite = false;
  material.needsUpdate = true;

  return {
    softDistance: uSoft as unknown as { value: number },
    cameraFar: uFar as unknown as { value: number },
  };
}
